/**
 * ------------------------------------------------------------------------------------
 * This client was automatically generated. ** Do Not Edit **
 * ------------------------------------------------------------------------------------
 */
import { useCallback, useRef } from 'react';
import * as Rudder from '../rudder-web';
import { usePageActions } from '@cvent/sli-nextjs-metrics';
import { ApiCallback } from '@rudderstack/analytics-js';
import { MeasureProperties, TrackProperties } from '../commonTypes';

export type ActionDefinition = {
  id: string;
  destinations?: string[];
};

export interface ActWebinarAttendeeLanding extends TrackProperties {
  // Unique anonymous Id assigned to each visitor to the landing page
  anonymousId: string;
  // Name of the event for the landing page
  eventName: string;
  // start date of the event
  eventStartDate: string;
  // End date of the event
  eventEndDate: string;
  // Number of speakers for the event
  numberOfSpeakers: number;
  // Status of event
  status?: string;
}
export interface ActWebinarAttendeeLandingRegistration extends MeasureProperties {
  // Unique anonymous Id assigned to each visitor to the landing page
  anonymousId: string;
  // Name of the event for the landing page
  eventName: string;
}
export interface AhPlayVideoCta extends TrackProperties {}
export interface AhSignUpCta extends TrackProperties {}
export interface EventAppAddToCalendar extends TrackProperties {
  // unique id of the of the session
  sessionId: string;
  // name of the session
  sessionName: string;
  // flag indicating if the session is featured session or not
  isFeatured: boolean;
  // the type of calendar they chose from the dropdown
  calendarType: string;
}

interface AnalyticsApi {
  actWebinarAttendeeLanding: (
    props: ActWebinarAttendeeLanding,
    options?: Rudder.Options,
    callback?: ApiCallback
  ) => void;
  startActWebinarAttendeeLandingRegistration: (
    props?: Partial<ActWebinarAttendeeLandingRegistration>,
    options?: Rudder.Options,
    callback?: ApiCallback
  ) => void;
  endActWebinarAttendeeLandingRegistration: (
    props?: Partial<ActWebinarAttendeeLandingRegistration>,
    options?: Rudder.Options,
    callback?: ApiCallback
  ) => void;
  ahPlayVideoCta: (props?: AhPlayVideoCta, options?: Rudder.Options, callback?: ApiCallback) => void;
  ahSignUpCta: (props?: AhSignUpCta, options?: Rudder.Options, callback?: ApiCallback) => void;
  eventAppAddToCalendar: (props: EventAppAddToCalendar, options?: Rudder.Options, callback?: ApiCallback) => void;
}

/**
 * This hook exposes a typed api with functions from the application's tracking plan.
 * Internally, each function uses the usePageActions API to track actions and forward
 * them to the reporters that were installed during the Metrics initialization in your
 * application's main entry point component.
 *
 * Note: This is the recommended way of tracking/measuring actions as it allows you to
 * register base properties during the metrics' client initialization, which will be included
 * automatically to every action whenever they are executed by your instrumentation code
 *
 *
 * How to use it:
 *
 * `const { acqSignupCompleted } = useWebinarPageActionsApi();`
 *
 *  // Somewhere in your application you can use this function to track this action
 * `acqSignupCompleted({ userId, email, accountName });`
 *
 * // If you want to track this action as an error you can do it with this piece of code below
 *  `acqSignupCompleted({ userId, email, error: '500 Error........' });`
 *
 * // If you want to track this action as an expected error you can do it with this piece of code below
 *  `acqSignupCompleted({ userId, email, expected_error: 'This email address is already associated with an existing account.' });`
 *
 * @returns AnalyticsApi
 */
export const useWebinarPageActionsApi = (): AnalyticsApi => {
  const measureActionsAttributesRef = useRef({} as Record<string, unknown>);
  const { startAction, endAction, trackAction, getBaseProps } = usePageActions();
  const { trackingPlanName } = getBaseProps() || {};

  /**
   * this action tracks the users who land on the Webinar public landing page in order to track the number of visits and unique visits to the Webinar
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbk1heF4MB087dnvZIaGorzNN
   * name          : attendee hub act_webinar_attendee_landing
   * downstreamName: act_webinar_attendee_landing
   * owner         : raptor
   * externalName  : Attendee lands on Webinar public landing page
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when users land on the Webinar public landing page
   * destinations  : Mixpanel,Analytics
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {ActWebinarAttendeeLanding} props - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const actWebinarAttendeeLanding = useCallback(
    (props: ActWebinarAttendeeLanding, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel,Analytics'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub act_webinar_attendee_landing',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"act_webinar_attendee_landing","name":"Attendee lands on Webinar public landing page","objectIdAttribute":"anonymousId","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '1-0-0',
          downstreamName: 'act_webinar_attendee_landing'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );
  /**
   * this action measures the time taken in the landing page to load the registratoin iframe
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbjyGQtad7gJ0ExjWrGJnQwlj
   * name          : attendee hub act_webinar_attendee_landing_registration
   * downstreamName: act_webinar_attendee_landing_registration
   * owner         : raptor
   * externalName  : Attendee lands on Webinar public landing page and registration completes loading
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : measure
   * multiStep     :  no
   * trigger       : the measurement starts when the landing page loads and ends when the registration iframe loads
   * destinations  : datadog
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {ActWebinarAttendeeLandingRegistration} [props] - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const startActWebinarAttendeeLandingRegistration = useCallback(
    (
      props?: Partial<ActWebinarAttendeeLandingRegistration>,
      options?: Rudder.Options,
      callback?: CallableFunction
    ): void => {
      const destinations = 'datadog'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub act_webinar_attendee_landing_registration',
        ...(destinations && { destinations })
      };
      measureActionsAttributesRef.current[action.id] = props;
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"act_webinar_attendee_landing_registration","name":"Attendee finishes loading registration on Webinar public landing page","objectIdAttribute":"anonymousId","type":"Action"},"datadog":{"eventId":"webinarRegistrationFormLoaded","name":"Webinar Registration Form Loaded"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;

      // Start measuring action via the pageActions API
      startAction(
        action,
        {
          ...props,
          version: '1-0-0',
          downstreamName: 'act_webinar_attendee_landing_registration'
        },
        callback,
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [startAction]
  );
  /**
   * Records action the measurement starts when the landing page loads and ends when the registration iframe loads
   */
  const endActWebinarAttendeeLandingRegistration = useCallback(
    (props?: Partial<ActWebinarAttendeeLandingRegistration>, options?: Rudder.Options): void => {
      const actionId = 'attendee hub act_webinar_attendee_landing_registration';
      // Record action via the pageActions API
      endAction(
        actionId,
        {
          ...props,
          ...getBaseProps(),
          version: '1-0-0',
          downstreamName: 'act_webinar_attendee_landing_registration'
        },
        false,
        { ...options, trackingPlanName, status: 'active' }
      );
    },
    [getBaseProps, endAction]
  );
  /**
   * this action tracks the users who clicks on the video on the branding block in order to gauge attendee interaction with the branding block
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbjvKq0iORsldHnaTq3OxwMfG
   * name          : attendee hub ah_play_video_cta
   * downstreamName: ah_play_video_cta
   * owner         : JTF2
   * externalName  : Branding block video play clicked
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when the attendee clicks the play button for the embedded youtube video on the branding block on the landing page
   * destinations  : Mixpanel
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {AhPlayVideoCta} [props] - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const ahPlayVideoCta = useCallback(
    (props?: AhPlayVideoCta, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub ah_play_video_cta',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"ah_play_video_cta","name":"ah_play_video_cta","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '1-0-0',
          downstreamName: 'ah_play_video_cta'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );
  /**
   * this action tracks the users who clicks the sign up link on the branding block in order to gauge attendee interaction with sign up
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbjxz1hJH0kXlB3OyFyo7LHai
   * name          : attendee hub ah_sign_up_cta
   * downstreamName: ah_sign_up_cta
   * owner         : JTF2
   * externalName  : Branding block self sign up link clicked
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when the attendee clicks the sign up button on the branding block on the landing page
   * destinations  : Mixpanel
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {AhSignUpCta} [props] - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const ahSignUpCta = useCallback(
    (props?: AhSignUpCta, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub ah_sign_up_cta',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"ah_sign_up_cta","name":"ah_sign_up_cta","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '1-0-0',
          downstreamName: 'ah_sign_up_cta'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );
  /**
   * this action tracks the users who clicks the add to calendar button in order to gauge interaction with the button
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            : ev_2VRbjuppkRjCiYr6svVfsdLl3bZ
   * name          : attendee hub event_app_add_to_calendar
   * downstreamName: event_app_add_to_calendar
   * owner         : JTF2
   * externalName  : Add to calendar
   * phase         : engagement
   * release       : v1.0
   * version       : 1-0-0
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when an attendee clicks the add to calendar button on the webinar info page or the session info page
   * destinations  : Mixpanel,Analytics,AttendeeActivities
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {EventAppAddToCalendar} props - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const eventAppAddToCalendar = useCallback(
    (props: EventAppAddToCalendar, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel,Analytics,AttendeeActivities'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub event_app_add_to_calendar',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"event_app_add_to_calendar","name":"Add to calendar","objectIdAttribute":"sessionId","activityType":"session.downloaded_calendar_reminder","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '1-0-0',
          downstreamName: 'event_app_add_to_calendar'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );

  return {
    actWebinarAttendeeLanding,
    startActWebinarAttendeeLandingRegistration,
    endActWebinarAttendeeLandingRegistration,
    ahPlayVideoCta,
    ahSignUpCta,
    eventAppAddToCalendar
  };
};
